<template>
  <div class="timber-page">
    <b-alert variant="danger" :show="noPermissions">You do not have permission to view Timber logs.</b-alert>
    <template v-if="!noPermissions">
    <div class="pk-page-header">
      <div class="pk-page-header__left">
        <img src="/img/developer/timber.svg" width="48" height="48" alt="Timber" />
        <div>
          <h1 class="pk-page-header__title">Timber</h1>
          <p class="pk-page-header__sub">Audit trail and application logs</p>
        </div>
      </div>
      <b-button variant="primary" size="sm" :disabled="loading" @click="refresh">
        <i class="fa fa-refresh" :class="{ 'fa-spin': loading }"></i>
      </b-button>
    </div>

    <!-- Metric cards -->
    <div class="timber-metrics">
      <div class="timber-metric">
        <div class="timber-metric__header">
          <span class="timber-metric__label">Total Events</span>
          <i class="fa fa-stream timber-metric__icon timber-metric__icon--blue"></i>
        </div>
        <strong class="timber-metric__value">{{ events.length }}</strong>
        <span class="timber-metric__sub">last {{ events.length >= 50 ? '50+' : events.length }} loaded</span>
      </div>
      <div class="timber-metric">
        <div class="timber-metric__header">
          <span class="timber-metric__label">Errors</span>
          <i class="fa fa-exclamation-circle timber-metric__icon timber-metric__icon--red"></i>
        </div>
        <strong class="timber-metric__value timber-metric__value--red">{{ errorCount }}</strong>
        <span class="timber-metric__sub">{{ errorRate }}% of total</span>
      </div>
      <div class="timber-metric">
        <div class="timber-metric__header">
          <span class="timber-metric__label">Warnings</span>
          <i class="fa fa-exclamation-triangle timber-metric__icon timber-metric__icon--yellow"></i>
        </div>
        <strong class="timber-metric__value timber-metric__value--yellow">{{ warnCount }}</strong>
        <span class="timber-metric__sub">{{ warnRate }}% of total</span>
      </div>
      <div class="timber-metric">
        <div class="timber-metric__header">
          <span class="timber-metric__label">Ingested</span>
          <i class="fa fa-database timber-metric__icon timber-metric__icon--teal"></i>
        </div>
        <strong class="timber-metric__value">{{ formatBytes(usage.bytes_ingested || 0) }}</strong>
        <span class="timber-metric__sub">this month</span>
      </div>
    </div>

    <!-- Charts row -->
    <div class="timber-charts">
      <!-- Volume timeline -->
      <div class="timber-chart-card timber-chart-card--wide">
        <div class="timber-chart-card__header">
          <h3>Event Volume</h3>
          <div class="timber-chart-legend">
            <span class="timber-chart-legend__item"><span class="timber-chart-legend__dot timber-chart-legend__dot--info"></span>Info</span>
            <span class="timber-chart-legend__item"><span class="timber-chart-legend__dot timber-chart-legend__dot--warn"></span>Warn</span>
            <span class="timber-chart-legend__item"><span class="timber-chart-legend__dot timber-chart-legend__dot--error"></span>Error</span>
            <span class="timber-chart-legend__item"><span class="timber-chart-legend__dot timber-chart-legend__dot--debug"></span>Debug</span>
          </div>
        </div>
        <div v-if="timelineBuckets.length === 0" class="timber-chart-empty">
          <span>No data to visualize</span>
        </div>
        <div v-else class="timber-timeline">
          <div class="timber-timeline__y-axis">
            <div class="timber-timeline__y-label">{{ timelineMax }}</div>
            <div class="timber-timeline__y-label">{{ Math.round(timelineMax * 0.5) }}</div>
            <div class="timber-timeline__y-label">0</div>
          </div>
          <div class="timber-timeline__area">
            <div class="timber-timeline__grid">
              <div class="timber-timeline__grid-line" v-for="i in 3" :key="i"></div>
            </div>
            <div class="timber-timeline__bars">
              <div
                v-for="(bucket, idx) in timelineBuckets"
                :key="idx"
                class="timber-timeline__col"
                @mouseenter="showTimelineTooltip(bucket, $event)"
                @mousemove="moveTimelineTooltip($event)"
                @mouseleave="hideTimelineTooltip"
              >
                <div class="timber-timeline__stack">
                  <div v-if="bucket.error" class="timber-timeline__bar timber-timeline__bar--error" :style="{ height: barHeight(bucket.error) }"></div>
                  <div v-if="bucket.warn" class="timber-timeline__bar timber-timeline__bar--warn" :style="{ height: barHeight(bucket.warn) }"></div>
                  <div v-if="bucket.info" class="timber-timeline__bar timber-timeline__bar--info" :style="{ height: barHeight(bucket.info) }"></div>
                  <div v-if="bucket.debug" class="timber-timeline__bar timber-timeline__bar--debug" :style="{ height: barHeight(bucket.debug) }"></div>
                </div>
                <span v-if="idx % timelineLabelStep === 0" class="timber-timeline__label">{{ bucket.label }}</span>
              </div>
            </div>
            <div v-if="timelineTooltip.visible" class="timber-tooltip" :style="{ top: timelineTooltip.y + 'px', left: timelineTooltip.x + 'px' }">
              <div class="timber-tooltip__title">{{ timelineTooltip.label }}</div>
              <div class="timber-tooltip__row" v-if="timelineTooltip.error"><span class="timber-chart-legend__dot timber-chart-legend__dot--error"></span>Error: <strong>{{ timelineTooltip.error }}</strong></div>
              <div class="timber-tooltip__row" v-if="timelineTooltip.warn"><span class="timber-chart-legend__dot timber-chart-legend__dot--warn"></span>Warn: <strong>{{ timelineTooltip.warn }}</strong></div>
              <div class="timber-tooltip__row" v-if="timelineTooltip.info"><span class="timber-chart-legend__dot timber-chart-legend__dot--info"></span>Info: <strong>{{ timelineTooltip.info }}</strong></div>
              <div class="timber-tooltip__row" v-if="timelineTooltip.debug"><span class="timber-chart-legend__dot timber-chart-legend__dot--debug"></span>Debug: <strong>{{ timelineTooltip.debug }}</strong></div>
              <div class="timber-tooltip__row timber-tooltip__row--total">Total: <strong>{{ timelineTooltip.total }}</strong></div>
            </div>
          </div>
        </div>
      </div>

      <!-- Level breakdown -->
      <div class="timber-chart-card">
        <div class="timber-chart-card__header">
          <h3>By Level</h3>
        </div>
        <div v-if="events.length === 0" class="timber-chart-empty">
          <span>No data</span>
        </div>
        <div v-else class="timber-breakdown">
          <div class="timber-breakdown__bar-wrap">
            <div class="timber-breakdown__bar">
              <div v-if="levelBreakdown.error" class="timber-breakdown__segment timber-breakdown__segment--error" :style="{ width: levelBreakdown.error + '%' }"></div>
              <div v-if="levelBreakdown.warn" class="timber-breakdown__segment timber-breakdown__segment--warn" :style="{ width: levelBreakdown.warn + '%' }"></div>
              <div v-if="levelBreakdown.info" class="timber-breakdown__segment timber-breakdown__segment--info" :style="{ width: levelBreakdown.info + '%' }"></div>
              <div v-if="levelBreakdown.debug" class="timber-breakdown__segment timber-breakdown__segment--debug" :style="{ width: levelBreakdown.debug + '%' }"></div>
            </div>
          </div>
          <div class="timber-breakdown__list">
            <div class="timber-breakdown__item" v-if="levelCounts.error">
              <span class="timber-chart-legend__dot timber-chart-legend__dot--error"></span>
              <span class="timber-breakdown__name">Error</span>
              <span class="timber-breakdown__count">{{ levelCounts.error }}</span>
              <span class="timber-breakdown__pct">{{ levelBreakdown.error }}%</span>
            </div>
            <div class="timber-breakdown__item" v-if="levelCounts.warn">
              <span class="timber-chart-legend__dot timber-chart-legend__dot--warn"></span>
              <span class="timber-breakdown__name">Warn</span>
              <span class="timber-breakdown__count">{{ levelCounts.warn }}</span>
              <span class="timber-breakdown__pct">{{ levelBreakdown.warn }}%</span>
            </div>
            <div class="timber-breakdown__item" v-if="levelCounts.info">
              <span class="timber-chart-legend__dot timber-chart-legend__dot--info"></span>
              <span class="timber-breakdown__name">Info</span>
              <span class="timber-breakdown__count">{{ levelCounts.info }}</span>
              <span class="timber-breakdown__pct">{{ levelBreakdown.info }}%</span>
            </div>
            <div class="timber-breakdown__item" v-if="levelCounts.debug">
              <span class="timber-chart-legend__dot timber-chart-legend__dot--debug"></span>
              <span class="timber-breakdown__name">Debug</span>
              <span class="timber-breakdown__count">{{ levelCounts.debug }}</span>
              <span class="timber-breakdown__pct">{{ levelBreakdown.debug }}%</span>
            </div>
          </div>

          <div class="timber-breakdown__services" v-if="topServices.length">
            <h4>Top Services</h4>
            <div class="timber-breakdown__item" v-for="svc in topServices" :key="svc.name">
              <span class="timber-breakdown__svc-dot"></span>
              <span class="timber-breakdown__name">{{ svc.name }}</span>
              <span class="timber-breakdown__count">{{ svc.count }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Event stream -->
    <div class="pk-card">
      <div class="timber-toolbar">
        <div class="timber-toolbar__search">
          <i class="fa fa-search timber-toolbar__search-icon"></i>
          <input
            v-model="filters.search"
            class="timber-toolbar__input"
            :placeholder="searchPlaceholder"
            @keyup.enter="refresh"
          />
        </div>
        <div class="timber-toolbar__filters">
          <b-form-select v-model="filters.source" :options="sourceOptions" size="sm" />
          <b-form-select v-model="filters.level" :options="levelOptions" size="sm" />
          <b-form-select v-model="filters.action" :options="actionOptions" size="sm" />
          <input
            type="datetime-local"
            v-model="filters.from"
            class="timber-toolbar__date-input"
            title="From"
            @change="refresh"
          />
          <span class="timber-toolbar__date-sep">–</span>
          <input
            type="datetime-local"
            v-model="filters.to"
            class="timber-toolbar__date-input"
            title="To"
            @change="refresh"
          />
          <button v-if="hasActiveFilters" class="timber-toolbar__clear" @click="resetFilters">
            <i class="fa fa-times"></i> Clear
          </button>
        </div>
      </div>
      <div class="timber-filter-toggles">
        <span class="timber-filter-toggles__label">Search in:</span>
        <label
          v-for="f in searchFieldOptions"
          :key="f.key"
          class="timber-filter-toggle"
          :class="{ 'timber-filter-toggle--active': searchFields[f.key] }"
        >
          <input type="checkbox" v-model="searchFields[f.key]" @change="onSearchFieldChange" />
          {{ f.label }}
        </label>
      </div>

      <b-alert v-if="error" show variant="danger" class="mx-3 mb-3">{{ error }}</b-alert>

      <div v-if="loading && events.length === 0" class="timber-empty">
        <i class="fa fa-spinner fa-spin timber-empty__icon"></i>
        <p>Loading events...</p>
      </div>

      <div v-else-if="events.length === 0" class="timber-empty">
        <i class="bi bi-inbox timber-empty__icon"></i>
        <p>No events found</p>
        <span class="timber-empty__hint">Events will appear here as they are generated</span>
      </div>

      <div v-else class="timber-events">
        <div
          v-for="event in events"
          :key="event.id || event.timestamp + event.resource_id"
          class="timber-event"
          :class="{ 'timber-event--expanded': expandedId === (event.id || event.timestamp + event.resource_id) }"
          @click="toggleExpand(event)"
        >
          <div class="timber-event__row">
            <span class="timber-level" :class="'level-' + (event.level || 'info')">{{ event.level || 'info' }}</span>
            <span class="timber-event__time">{{ formatTime(event.timestamp) }}</span>
            <span class="timber-event__service" v-if="event.service">{{ event.service }}</span>
            <span class="timber-event__action" v-if="event.action">{{ event.action }}</span>
            <span class="timber-event__actor" v-if="event.actor && event.actor.entity && event.actor.entity.name">{{ event.actor.entity.name }}</span>
            <span class="timber-event__message">{{ event.message || summarize(event) }}</span>
            <i class="fa fa-chevron-down timber-event__chevron"></i>
          </div>

          <transition name="timber-expand">
            <div v-if="expandedId === (event.id || event.timestamp + event.resource_id)" class="timber-event__detail" @click.stop>
              <div class="timber-detail-grid">
                <div class="timber-detail-item" v-if="event.timestamp">
                  <span class="timber-detail-item__label">Timestamp</span>
                  <span class="timber-detail-item__value">{{ new Date(event.timestamp).toLocaleString() }}</span>
                </div>
                <div class="timber-detail-item" v-if="event.level">
                  <span class="timber-detail-item__label">Level</span>
                  <span class="timber-detail-item__value">{{ event.level }}</span>
                </div>
                <div class="timber-detail-item" v-if="event.service">
                  <span class="timber-detail-item__label">Service</span>
                  <span class="timber-detail-item__value">{{ event.service }}</span>
                </div>
                <div class="timber-detail-item" v-if="event.action">
                  <span class="timber-detail-item__label">Action</span>
                  <span class="timber-detail-item__value">{{ event.action }}</span>
                </div>
                <div class="timber-detail-item" v-if="event.request_uuid">
                  <span class="timber-detail-item__label">Request UUID</span>
                  <span class="timber-detail-item__value">{{ event.request_uuid }}</span>
                </div>
                <div class="timber-detail-item" v-if="event.resource_name || event.resource_id">
                  <span class="timber-detail-item__label">Resource</span>
                  <span class="timber-detail-item__value">
                    {{ event.resource_name || event.resource_id }}
                    <small v-if="event.resource_type" class="timber-detail-item__sub">{{ event.resource_type }}</small>
                  </span>
                </div>
                <div class="timber-detail-item" v-if="event.actor">
                  <span class="timber-detail-item__label">Actor</span>
                  <span class="timber-detail-item__value">
                    {{ actorName(event.actor) }}
                    <small v-if="event.actor && event.actor.type" class="timber-detail-item__sub">{{ event.actor.type }}</small>
                  </span>
                </div>
                <div class="timber-detail-item" v-if="event.source">
                  <span class="timber-detail-item__label">Source</span>
                  <span class="timber-detail-item__value">{{ event.source }}</span>
                </div>
                <div class="timber-detail-item" v-if="event.trace_id">
                  <span class="timber-detail-item__label">Trace ID</span>
                  <span class="timber-detail-item__value">{{ event.trace_id }}</span>
                </div>
                <div class="timber-detail-item" v-if="event.span_id">
                  <span class="timber-detail-item__label">Span ID</span>
                  <span class="timber-detail-item__value">{{ event.span_id }}</span>
                </div>
              </div>
              <div v-if="event.message" class="timber-detail-message">
                <span class="timber-detail-item__label">Message</span>
                <p>{{ event.message }}</p>
              </div>
              <div v-if="event.attributes && Object.keys(event.attributes).length" class="timber-detail-attributes">
                <span class="timber-detail-item__label">Attributes</span>
                <div class="timber-attributes">
                  <div
                    v-for="(val, key) in event.attributes"
                    :key="key"
                    class="timber-attributes__entry"
                    :class="{ 'timber-attributes__entry--expandable': isLongValue(val), 'timber-attributes__entry--expanded': expandedAttr === (expandedId + ':' + key) }"
                    @click.stop="toggleAttr(key)"
                  >
                    <span class="timber-attributes__key">{{ key }}</span>
                    <span class="timber-attributes__value">{{ formatAttrValue(val) }}</span>
                    <button
                      v-if="expandedAttr === (expandedId + ':' + key)"
                      class="timber-attributes__copy"
                      @click.stop="copyAttrValue(val)"
                      :title="copiedAttr === (expandedId + ':' + key) ? 'Copied!' : 'Copy value'"
                    >
                      <i :class="copiedAttr === (expandedId + ':' + key) ? 'fa fa-check' : 'fa fa-copy'"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </transition>
        </div>
      </div>

      <div class="timber-footer" v-if="meta.has_more">
        <b-button variant="outline-primary" size="sm" :disabled="loadingMore" @click="loadMore">
          {{ loadingMore ? 'Loading...' : 'Load more events' }}
        </b-button>
      </div>
      <div class="timber-footer" v-else-if="events.length > 0">
        <span class="timber-footer__count">{{ events.length }} event{{ events.length !== 1 ? 's' : '' }} loaded</span>
      </div>
    </div>
    </template>
  </div>
</template>

<script>
export default {
  name: 'TimberIndex',
  data: function () {
    return {
      noPermissions: false,
      loading: false,
      loadingMore: false,
      expandedId: null,
      expandedAttr: null,
      copiedAttr: null,
      error: '',
      events: [],
      meta: {
        has_more: false,
        next_cursor: ''
      },
      usage: {},
      filters: {
        search: '',
        source: '',
        level: '',
        action: '',
        from: '',
        to: ''
      },
      searchFields: {
        actor_id: false,
        actor_name: false,
        resource_id: false,
        request_uuid: false
      },
      searchFieldOptions: [
        { key: 'actor_id', label: 'Actor ID' },
        { key: 'actor_name', label: 'Actor name' },
        { key: 'resource_id', label: 'Entity ID' },
        { key: 'request_uuid', label: 'Request UUID' }
      ],
      timelineTooltip: {
        visible: false,
        x: 0,
        y: 0,
        label: '',
        info: 0,
        warn: 0,
        error: 0,
        debug: 0,
        total: 0
      },
      sourceOptions: [
        { value: '', text: 'All sources' },
        { value: 'system', text: 'System' },
        { value: 'user', text: 'User app' }
      ],
      levelOptions: [
        { value: '', text: 'All levels' },
        { value: 'debug', text: 'Debug' },
        { value: 'info', text: 'Info' },
        { value: 'warn', text: 'Warn' },
        { value: 'error', text: 'Error' }
      ],
      actionOptions: [
        { value: '', text: 'All actions' },
        { value: 'create', text: 'Create' },
        { value: 'update', text: 'Update' },
        { value: 'delete', text: 'Delete' },
        { value: 'execute', text: 'Execute' },
        { value: 'deploy', text: 'Deploy' },
        { value: 'archive', text: 'Archive' }
      ]
    }
  },
  computed: {
    hasActiveFilters: function () {
      return this.filters.search || this.filters.source || this.filters.level || this.filters.action || this.filters.from || this.filters.to
    },
    searchPlaceholder: function () {
      var self = this
      var active = this.searchFieldOptions.filter(function (f) { return self.searchFields[f.key] })
      if (active.length === 0) return 'Search messages, resources, actors...'
      return 'Search by ' + active.map(function (f) { return f.label }).join(', ') + '...'
    },
    errorCount: function () {
      return this.events.filter(function (e) { return e.level === 'error' }).length
    },
    warnCount: function () {
      return this.events.filter(function (e) { return e.level === 'warn' }).length
    },
    errorRate: function () {
      if (!this.events.length) return 0
      return Math.round((this.errorCount / this.events.length) * 100)
    },
    warnRate: function () {
      if (!this.events.length) return 0
      return Math.round((this.warnCount / this.events.length) * 100)
    },
    levelCounts: function () {
      var counts = { error: 0, warn: 0, info: 0, debug: 0 }
      this.events.forEach(function (e) {
        var lvl = e.level || 'info'
        if (counts[lvl] !== undefined) counts[lvl]++
      })
      return counts
    },
    levelBreakdown: function () {
      var total = this.events.length || 1
      var c = this.levelCounts
      return {
        error: Math.round((c.error / total) * 100),
        warn: Math.round((c.warn / total) * 100),
        info: Math.round((c.info / total) * 100),
        debug: Math.round((c.debug / total) * 100)
      }
    },
    topServices: function () {
      var map = {}
      this.events.forEach(function (e) {
        if (e.service) {
          map[e.service] = (map[e.service] || 0) + 1
        }
      })
      return Object.keys(map)
        .map(function (name) { return { name: name, count: map[name] } })
        .sort(function (a, b) { return b.count - a.count })
        .slice(0, 5)
    },
    timelineBuckets: function () {
      if (!this.events.length) return []
      var sorted = this.events.slice().sort(function (a, b) {
        return new Date(a.timestamp) - new Date(b.timestamp)
      })
      var first = new Date(sorted[0].timestamp)
      var last = new Date(sorted[sorted.length - 1].timestamp)
      var spanMs = last - first
      var bucketMs, fmt
      if (spanMs <= 3600000) {
        bucketMs = 300000
        fmt = 'minute'
      } else if (spanMs <= 86400000) {
        bucketMs = 3600000
        fmt = 'hour'
      } else {
        bucketMs = 86400000
        fmt = 'day'
      }
      var startT = Math.floor(first.getTime() / bucketMs) * bucketMs
      var endT = Math.floor(last.getTime() / bucketMs) * bucketMs + bucketMs
      var buckets = []
      for (var t = startT; t < endT; t += bucketMs) {
        buckets.push({ t: t, info: 0, warn: 0, error: 0, debug: 0, label: this.bucketLabel(t, fmt) })
      }
      sorted.forEach(function (e) {
        var eT = new Date(e.timestamp).getTime()
        var idx = Math.floor((eT - startT) / bucketMs)
        if (idx >= 0 && idx < buckets.length) {
          var lvl = e.level || 'info'
          if (buckets[idx][lvl] !== undefined) buckets[idx][lvl]++
        }
      })
      return buckets
    },
    timelineMax: function () {
      var max = 0
      this.timelineBuckets.forEach(function (b) {
        var total = b.info + b.warn + b.error + b.debug
        if (total > max) max = total
      })
      return Math.max(max, 1)
    },
    timelineLabelStep: function () {
      var n = this.timelineBuckets.length
      if (n > 24) return Math.ceil(n / 8)
      if (n > 12) return 2
      return 1
    }
  },
  mounted: function () {
    if (!this.$can('manage timber')) {
      this.noPermissions = true
      return
    }
    this.refresh()
    this.fetchUsage()
  },
  methods: {
    refresh: function () {
      this.loading = true
      this.error = ''
      this.fetchEvents().finally(function () {
        this.loading = false
      }.bind(this))
    },
    fetchEvents: function (cursor) {
      var self = this
      var params = Object.assign({ limit: 50 }, this.cleanFilters())
      if (cursor) {
        params.cursor = cursor
      }
      return this.$integrationApi.get('/timber/logs', { params: params }).then(function (resp) {
        var data = resp.data && resp.data.data ? resp.data.data : []
        self.meta = resp.data && resp.data.meta ? resp.data.meta : {}
        self.events = cursor ? self.events.concat(data) : data
      }).catch(function (err) {
        self.error = self.errorMessage(err, 'Failed to load Timber events')
      })
    },
    fetchUsage: function () {
      var self = this
      return this.$integrationApi.get('/timber/usage').then(function (resp) {
        self.usage = resp.data || {}
      }).catch(function () {
        self.usage = { tier: 'free', bytes_ingested: 0 }
      })
    },
    loadMore: function () {
      if (!this.meta.next_cursor) return
      this.loadingMore = true
      this.fetchEvents(this.meta.next_cursor).finally(function () {
        this.loadingMore = false
      }.bind(this))
    },
    toggleExpand: function (event) {
      var id = event.id || event.timestamp + event.resource_id
      this.expandedId = this.expandedId === id ? null : id
    },
    resetFilters: function () {
      this.filters = { search: '', source: '', level: '', action: '', from: '', to: '' }
      this.searchFields = { actor_id: false, actor_name: false, resource_id: false, request_uuid: false }
      this.refresh()
    },
    onSearchFieldChange: function () {
      if (this.filters.search) this.refresh()
    },
    cleanFilters: function () {
      var clean = {}
      if (this.filters.source) clean.source = this.filters.source
      if (this.filters.level) clean.level = this.filters.level
      if (this.filters.action) clean.action = this.filters.action
      if (this.filters.from) clean.from = new Date(this.filters.from).toISOString()
      if (this.filters.to) clean.to = new Date(this.filters.to).toISOString()
      if (this.filters.search) {
        var self = this
        var targeted = this.searchFieldOptions.filter(function (f) { return self.searchFields[f.key] })
        if (targeted.length > 0) {
          targeted.forEach(function (f) { clean[f.key] = self.filters.search })
        } else {
          clean.search = this.filters.search
        }
      }
      return clean
    },
    bucketLabel: function (timestamp, fmt) {
      var d = new Date(timestamp)
      if (fmt === 'minute') return d.getHours() + ':' + String(d.getMinutes()).padStart(2, '0')
      if (fmt === 'hour') return d.getHours() + ':00'
      return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
    },
    barHeight: function (count) {
      if (!this.timelineMax) return '0%'
      return Math.max((count / this.timelineMax) * 100, 2) + '%'
    },
    showTimelineTooltip: function (bucket, event) {
      this.timelineTooltip.visible = true
      this.timelineTooltip.label = bucket.label
      this.timelineTooltip.info = bucket.info
      this.timelineTooltip.warn = bucket.warn
      this.timelineTooltip.error = bucket.error
      this.timelineTooltip.debug = bucket.debug
      this.timelineTooltip.total = bucket.info + bucket.warn + bucket.error + bucket.debug
      this.moveTimelineTooltip(event)
    },
    moveTimelineTooltip: function (event) {
      var x = event.clientX + 12
      var y = event.clientY - 100
      if (x + 180 > window.innerWidth) x = event.clientX - 190
      if (y < 4) y = event.clientY + 12
      this.timelineTooltip.x = x
      this.timelineTooltip.y = y
    },
    hideTimelineTooltip: function () {
      this.timelineTooltip.visible = false
    },
    formatTime: function (value) {
      if (!value) return '-'
      var date = new Date(value)
      var now = new Date()
      var diffMs = now - date
      var diffSec = Math.floor(diffMs / 1000)
      var diffMin = Math.floor(diffSec / 60)
      var diffHr = Math.floor(diffMin / 60)
      if (diffSec < 60) return 'just now'
      if (diffMin < 60) return diffMin + 'm ago'
      if (diffHr < 24) return diffHr + 'h ago'
      if (diffHr < 48) return 'yesterday'
      return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
    },
    formatBytes: function (bytes) {
      if (!bytes) return '0 B'
      var units = ['B', 'KB', 'MB', 'GB']
      var size = Number(bytes)
      var unit = 0
      while (size >= 1024 && unit < units.length - 1) {
        size = size / 1024
        unit++
      }
      return size.toFixed(unit === 0 ? 0 : 1) + ' ' + units[unit]
    },
    actorName: function (actor) {
      if (!actor) return '-'
      return actor.name || actor.id || '-'
    },
    summarize: function (event) {
      var bits = [event.service, event.action, event.resource_type].filter(Boolean)
      return bits.length ? bits.join(' / ') : '-'
    },
    formatAttrValue: function (val) {
      if (val === null || val === undefined) return '-'
      if (typeof val === 'object') return JSON.stringify(val, null, 2)
      return String(val)
    },
    isLongValue: function (val) {
      var str = this.formatAttrValue(val)
      return str.length > 80 || str.indexOf('\n') !== -1
    },
    toggleAttr: function (key) {
      var id = this.expandedId + ':' + key
      this.expandedAttr = this.expandedAttr === id ? null : id
      this.copiedAttr = null
    },
    copyAttrValue: function (val) {
      var self = this
      var text = this.formatAttrValue(val)
      navigator.clipboard.writeText(text).then(function () {
        self.copiedAttr = self.expandedAttr
        setTimeout(function () { self.copiedAttr = null }, 2000)
      })
    },
    errorMessage: function (err, fallback) {
      return err && err.response && err.response.data && err.response.data.message
        ? err.response.data.message
        : fallback
    }
  }
}
</script>

<style scoped>
.timber-page {
  padding: 0;
}

/* ── Metric cards ── */
.timber-metrics {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  margin-bottom: 16px;
}

.timber-metric {
  background: var(--bg-card);
  border: 1px solid var(--border-default);
  border-radius: 10px;
  padding: 16px 18px;
}

.timber-metric__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}

.timber-metric__label {
  font-size: 11px;
  color: var(--text-muted);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 600;
}

.timber-metric__icon {
  font-size: 14px;
}

.timber-metric__icon--blue { color: #818cf8; }
.timber-metric__icon--red { color: #f87171; }
.timber-metric__icon--yellow { color: #fbbf24; }
.timber-metric__icon--teal { color: #4fd1c7; }

.timber-metric__value {
  display: block;
  font-size: 28px;
  color: var(--text-primary);
  line-height: 1.1;
  font-weight: 700;
}

.timber-metric__value--red { color: #f87171; }
.timber-metric__value--yellow { color: #fbbf24; }

.timber-metric__sub {
  font-size: 11px;
  color: var(--text-muted);
  margin-top: 2px;
  display: block;
}

/* ── Charts row ── */
.timber-charts {
  display: grid;
  grid-template-columns: 1fr 320px;
  gap: 12px;
  margin-bottom: 16px;
}

.timber-chart-card {
  background: var(--bg-card);
  border: 1px solid var(--border-default);
  border-radius: 10px;
  padding: 18px;
}

.timber-chart-card__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}

.timber-chart-card__header h3 {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
}

.timber-chart-legend {
  display: flex;
  gap: 12px;
}

.timber-chart-legend__item {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 11px;
  color: var(--text-muted);
}

.timber-chart-legend__dot {
  width: 8px;
  height: 8px;
  border-radius: 2px;
  flex-shrink: 0;
}

.timber-chart-legend__dot--info { background: #818cf8; }
.timber-chart-legend__dot--warn { background: #fbbf24; }
.timber-chart-legend__dot--error { background: #f87171; }
.timber-chart-legend__dot--debug { background: #64748b; }

.timber-chart-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 180px;
  color: var(--text-muted);
  font-size: 13px;
}

/* ── Timeline chart ── */
.timber-timeline {
  display: flex;
  gap: 6px;
  height: 200px;
}

.timber-timeline__y-axis {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 30px;
  padding-bottom: 22px;
}

.timber-timeline__y-label {
  font-size: 10px;
  color: var(--text-muted);
  text-align: right;
  line-height: 1;
}

.timber-timeline__area {
  flex: 1;
  position: relative;
}

.timber-timeline__grid {
  position: absolute;
  inset: 0;
  bottom: 22px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  pointer-events: none;
}

.timber-timeline__grid-line {
  border-bottom: 1px solid var(--border-separator);
}

.timber-timeline__bars {
  position: absolute;
  inset: 0;
  display: flex;
  gap: 3px;
  padding: 0 2px;
}

.timber-timeline__col {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 0;
  cursor: pointer;
}

.timber-timeline__stack {
  flex: 1 1 auto;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: center;
  padding-bottom: 0;
}

.timber-timeline__bar {
  width: 80%;
  max-width: 24px;
  min-height: 2px;
  border-radius: 2px 2px 0 0;
  transition: opacity 0.15s;
}

.timber-timeline__col:hover .timber-timeline__bar {
  opacity: 0.8;
}

.timber-timeline__bar--info { background: #818cf8; }
.timber-timeline__bar--warn { background: #fbbf24; }
.timber-timeline__bar--error { background: #f87171; }
.timber-timeline__bar--debug { background: #64748b; }

.timber-timeline__label {
  font-size: 10px;
  color: var(--text-muted);
  white-space: nowrap;
  margin-top: 4px;
  flex-shrink: 0;
  height: 18px;
}

/* ── Tooltip ── */
.timber-tooltip {
  position: fixed;
  z-index: 9999;
  background: #1e2433;
  border: 1px solid var(--border-default);
  border-radius: 8px;
  padding: 10px 14px;
  pointer-events: none;
  min-width: 150px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
}

.timber-tooltip__title {
  font-size: 12px;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 6px;
}

.timber-tooltip__row {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: var(--text-secondary);
  margin-top: 3px;
}

.timber-tooltip__row--total {
  margin-top: 6px;
  padding-top: 6px;
  border-top: 1px solid var(--border-separator);
  color: var(--text-primary);
}

/* ── Level breakdown ── */
.timber-breakdown {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.timber-breakdown__bar-wrap {
  padding: 4px 0;
}

.timber-breakdown__bar {
  display: flex;
  height: 10px;
  border-radius: 5px;
  overflow: hidden;
  background: var(--form-bg);
}

.timber-breakdown__segment {
  transition: width 0.3s ease;
  min-width: 4px;
}

.timber-breakdown__segment--error { background: #f87171; }
.timber-breakdown__segment--warn { background: #fbbf24; }
.timber-breakdown__segment--info { background: #818cf8; }
.timber-breakdown__segment--debug { background: #64748b; }

.timber-breakdown__list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.timber-breakdown__item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
}

.timber-breakdown__name {
  color: var(--text-secondary);
  flex: 1;
}

.timber-breakdown__count {
  color: var(--text-primary);
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}

.timber-breakdown__pct {
  color: var(--text-muted);
  font-size: 11px;
  min-width: 32px;
  text-align: right;
}

.timber-breakdown__services {
  border-top: 1px solid var(--border-separator);
  padding-top: 14px;
}

.timber-breakdown__services h4 {
  font-size: 12px;
  color: var(--text-muted);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin: 0 0 10px;
  font-weight: 600;
}

.timber-breakdown__svc-dot {
  width: 8px;
  height: 8px;
  border-radius: 2px;
  background: var(--primary-start);
  flex-shrink: 0;
}

/* ── Toolbar ── */
.timber-toolbar {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  border-bottom: 1px solid var(--border-separator);
}

.timber-toolbar__search {
  position: relative;
  flex: 1;
  min-width: 200px;
}

.timber-toolbar__search-icon {
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--text-muted);
  font-size: 12px;
  pointer-events: none;
}

.timber-toolbar__input {
  width: 100%;
  background: var(--form-bg);
  border: 1px solid var(--form-border);
  border-radius: var(--radius-md);
  color: var(--text-primary);
  padding: 6px 10px 6px 30px;
  font-size: 13px;
  transition: var(--transition-quick);
}

.timber-toolbar__input:focus {
  border-color: var(--border-active);
  box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.1);
  outline: none;
}

.timber-toolbar__input::placeholder {
  color: var(--text-muted);
}

.timber-toolbar__filters {
  display: flex;
  align-items: center;
  gap: 8px;
}

.timber-toolbar__filters .custom-select {
  min-width: 120px;
  font-size: 13px;
}

.timber-toolbar__filter-input {
  width: 180px;
  background: var(--form-bg);
  border: 1px solid var(--form-border);
  border-radius: var(--radius-md);
  color: var(--text-primary);
  padding: 5px 10px;
  font-size: 13px;
}

.timber-toolbar__filter-input:focus {
  border-color: var(--border-active);
  box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.1);
  outline: none;
}

.timber-toolbar__date-input {
  background: var(--form-bg);
  border: 1px solid var(--form-border);
  border-radius: var(--radius-md);
  color: var(--text-primary);
  padding: 4px 8px;
  font-size: 12px;
  max-width: 180px;
  transition: var(--transition-quick);
}

.timber-toolbar__date-input:focus {
  border-color: var(--border-active);
  box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.1);
  outline: none;
}

.timber-toolbar__date-sep {
  color: var(--text-muted);
  font-size: 12px;
}

.timber-toolbar__clear {
  background: none;
  border: 1px solid var(--form-border);
  border-radius: var(--radius-md);
  color: var(--text-muted);
  padding: 4px 10px;
  font-size: 12px;
  cursor: pointer;
  transition: var(--transition-quick);
  white-space: nowrap;
}

.timber-toolbar__clear:hover {
  border-color: var(--color-error-alt);
  color: var(--color-error-alt);
}

/* ── Filter toggles ── */
.timber-filter-toggles {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 6px 16px;
  border-bottom: 1px solid var(--border-separator);
}

.timber-filter-toggles__label {
  font-size: 11px;
  color: var(--text-muted);
  text-transform: uppercase;
  letter-spacing: 0.4px;
  font-weight: 600;
}

.timber-filter-toggle {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  color: var(--text-muted);
  cursor: pointer;
  user-select: none;
  transition: color 0.15s;
}

.timber-filter-toggle:hover {
  color: var(--text-secondary);
}

.timber-filter-toggle--active {
  color: var(--text-primary);
}

.timber-filter-toggle input[type="checkbox"] {
  margin: 0;
  accent-color: var(--primary-start);
}

/* ── Empty state ── */
.timber-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: var(--text-muted);
}

.timber-empty__icon {
  font-size: 32px;
  margin-bottom: 12px;
  opacity: 0.4;
}

.timber-empty p {
  margin: 0;
  font-size: 15px;
  color: var(--text-secondary);
}

.timber-empty__hint {
  font-size: 13px;
  margin-top: 4px;
}

/* ── Event list ── */
.timber-event {
  border-bottom: 1px solid var(--border-separator);
  cursor: pointer;
  transition: background 0.15s;
}

.timber-event:last-child {
  border-bottom: none;
}

.timber-event:hover {
  background: rgba(255, 255, 255, 0.02);
}

.timber-event--expanded {
  background: rgba(102, 126, 234, 0.04);
}

.timber-event__row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 16px;
  min-height: 42px;
}

.timber-event__time {
  font-size: 12px;
  color: var(--text-muted);
  min-width: 70px;
  flex-shrink: 0;
}

.timber-event__service {
  font-size: 12px;
  color: var(--text-secondary);
  background: rgba(255, 255, 255, 0.04);
  padding: 2px 8px;
  border-radius: 4px;
  flex-shrink: 0;
}

.timber-event__action {
  font-size: 12px;
  color: var(--primary-start);
  font-weight: 500;
  flex-shrink: 0;
}

.timber-event__actor {
  font-size: 12px;
  color: var(--text-muted);
  flex-shrink: 0;
  max-width: 150px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.timber-event__message {
  font-size: 13px;
  color: var(--text-primary);
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.timber-event__chevron {
  font-size: 10px;
  color: var(--text-muted);
  transition: transform 0.2s;
  flex-shrink: 0;
}

.timber-event--expanded .timber-event__chevron {
  transform: rotate(180deg);
}

/* ── Level badges ── */
.timber-level {
  border-radius: 4px;
  display: inline-block;
  font-size: 10px;
  font-weight: 600;
  min-width: 42px;
  padding: 2px 6px;
  text-align: center;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  flex-shrink: 0;
}

.level-debug { background: rgba(148, 163, 184, 0.2); color: #94a3b8; }
.level-info  { background: rgba(99, 102, 241, 0.2);  color: #818cf8; }
.level-warn  { background: rgba(245, 158, 11, 0.2);  color: #f59e0b; }
.level-error { background: rgba(239, 68, 68, 0.2);   color: #f87171; }

/* ── Expanded detail ── */
.timber-event__detail {
  padding: 0 16px 14px 68px;
}

.timber-detail-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 12px;
  margin-bottom: 8px;
}

.timber-detail-item__label {
  display: block;
  font-size: 10px;
  color: var(--text-muted);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 2px;
}

.timber-detail-item__value {
  font-size: 13px;
  color: var(--text-primary);
}

.timber-detail-item__sub {
  display: block;
  font-size: 11px;
  color: var(--text-muted);
  margin-top: 1px;
}

.timber-detail-message {
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid var(--border-separator);
}

.timber-detail-message p {
  font-size: 13px;
  color: var(--text-secondary);
  margin: 4px 0 0;
  white-space: pre-wrap;
  word-break: break-word;
}

.timber-detail-attributes {
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid var(--border-separator);
}

.timber-attributes {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 6px;
}

.timber-attributes__entry {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid var(--border-separator);
  border-radius: 4px;
  padding: 3px 8px;
  font-size: 12px;
}

.timber-attributes__key {
  color: var(--text-muted);
  font-weight: 500;
}

.timber-attributes__key::after {
  content: ':';
}

.timber-attributes__value {
  color: var(--text-primary);
  word-break: break-word;
  max-width: 300px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.timber-attributes__entry--expandable {
  cursor: pointer;
}

.timber-attributes__entry--expandable:hover {
  border-color: var(--border-active);
}

.timber-attributes__entry--expanded {
  flex-basis: 100%;
  max-width: 100%;
}

.timber-attributes__entry--expanded .timber-attributes__value {
  max-width: none;
  white-space: pre-wrap;
  overflow: visible;
  text-overflow: unset;
}

.timber-attributes__copy {
  background: none;
  border: 1px solid var(--border-separator);
  border-radius: 4px;
  color: var(--text-muted);
  padding: 2px 6px;
  font-size: 11px;
  cursor: pointer;
  flex-shrink: 0;
  align-self: flex-start;
  margin-left: auto;
  transition: var(--transition-quick);
}

.timber-attributes__copy:hover {
  color: var(--text-primary);
  border-color: var(--border-active);
}

/* ── Expand transition ── */
.timber-expand-enter-active,
.timber-expand-leave-active {
  transition: all 0.2s ease;
  overflow: hidden;
}

.timber-expand-enter,
.timber-expand-leave-to {
  opacity: 0;
  max-height: 0;
  padding-top: 0;
  padding-bottom: 0;
}

/* ── Footer ── */
.timber-footer {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 14px;
  border-top: 1px solid var(--border-separator);
}

.timber-footer__count {
  font-size: 12px;
  color: var(--text-muted);
}

/* ── Responsive ── */
@media (max-width: 900px) {
  .timber-metrics {
    grid-template-columns: repeat(2, 1fr);
  }

  .timber-charts {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 600px) {
  .timber-metrics {
    grid-template-columns: 1fr;
  }

  .timber-toolbar {
    flex-direction: column;
    align-items: stretch;
  }

  .timber-toolbar__filters {
    flex-wrap: wrap;
  }

  .timber-event__row {
    flex-wrap: wrap;
    gap: 6px;
  }

  .timber-event__message {
    flex-basis: 100%;
    order: 10;
  }

  .timber-event__detail {
    padding-left: 16px;
  }
}
</style>
